import { FC, useEffect, useState } from "react";
import { gql, useApolloClient, useLazyQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import {
  Cell,
  Row,
  Table,
  ButtonVariantEnums,
  IconButton,
  Icon,
  IDialogKeyEnums,
  useDialogs,
  useLocaleFromLanguage,
  LoadingIndicator,
  LoaderTypeEnums,
  IconNameEnums,
  IconSizeEnums,
} from "@canei/app-components";
import { useSelector } from "react-redux";
import { StyledDownloadInvoices } from "./styled.download-invoices";
import { ILocalState } from "../../../../@types";
import { isEqual } from "lodash";
import { invoiceLockKey, isInvoiceTabOpening, openInvoiceTab } from "./openInvoiceTab";
import { invoiceTabMessage } from "./invoiceTabMessage";

interface IInvoices {
  number: string;
  created: string;
  invoice_pdf: string;
}
const GET_INVOICES = gql`
  query getInvoices($customer_id: ID!) {
    getInvoices(customer_id: $customer_id) {
      number
      created
      invoice_pdf
    }
  }
`;

/**
 * Adresse des bei uns abgelegten Rechnungsbelegs.
 *
 * Liefert null, wenn es keinen gibt - der NORMALE Fall fuer jede Rechnung vor
 * der Umstellung. Der Aufrufer faellt dann auf `invoice_pdf` zurueck.
 */
const GET_INVOICE_DOCUMENT_URL = gql`
  query getInvoiceDocumentUrl($customer_id: ID!, $invoice_number: String!) {
    result: getInvoiceDocumentUrl(customer_id: $customer_id, invoice_number: $invoice_number)
  }
`;
export interface IDownloadInvoice {
  getInvoices: IInvoices[];
}
export interface IDownloadInvoiceVar {
  customer_id: string;
}
interface IInvoiceDocumentUrl {
  result: string | null;
}
interface IInvoiceDocumentUrlVar {
  customer_id: string;
  invoice_number: string;
}
export const Content: FC = () => {
  const { t } = useTranslation(["quick/common"]);
  const { localeFromLanguage } = useLocaleFromLanguage();
  /**
   * Welche Zeilen gerade auf ihren Beleg warten - NUR fuers Rendern.
   *
   * Die eigentliche Sperre liegt modulweit in `openInvoiceTab`; ein Zustand
   * aus `useState` taugt dafuer nicht, weil er erst beim naechsten Rendern
   * sichtbar wird und der Doppelklick genau dazwischen liegt.
   */
  const [openingKeys, setOpeningKeys] = useState<string[]>([]);
  const [openError, setOpenError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<IInvoices[] | undefined>([]);
  const currentUser = useSelector(({ currentUser }: ILocalState) => currentUser, isEqual);
  const apolloClient = useApolloClient();
  const [getInvoices, { loading }] = useLazyQuery<IDownloadInvoice, IDownloadInvoiceVar>(
    GET_INVOICES,
    {
      onCompleted: (data) => {
        setInvoices(data.getInvoices);
      },
      fetchPolicy: "no-cache",
    }
  );
  /**
   * Holt die Adresse unseres abgelegten Belegs zu einer Rechnungsnummer.
   *
   * Ohne Kundennummer gibt es keinen Ablageschluessel; dann gar nicht erst
   * fragen, sondern null melden, damit auf Stripes PDF zurueckgefallen wird.
   *
   * Bewusst `apolloClient.query` statt `useLazyQuery`: Der Abruf haengt am
   * Klick und muss ein Promise liefern - in @apollo/client 3.4 gibt die
   * Ausfuehrfunktion von `useLazyQuery` noch nichts zurueck.
   */
  const fetchDocumentUrl = (invoiceNumber: string): Promise<string | null> => {
    const customerId = currentUser.appUser.customer_id;
    if (!customerId) return Promise.resolve(null);

    return apolloClient
      .query<IInvoiceDocumentUrl, IInvoiceDocumentUrlVar>({
        query: GET_INVOICE_DOCUMENT_URL,
        variables: { customer_id: customerId, invoice_number: invoiceNumber },
        fetchPolicy: "no-cache",
      })
      .then(({ data }) => data?.result ?? null);
  };

  /**
   * Oeffnet den Beleg zu einer Rechnung in einem neuen Tab.
   *
   * Frueher wurde hier ein <a href download> erzeugt und geklickt. Bei einer
   * Adresse fremder Herkunft - und die signierte S3-Adresse ist immer fremd -
   * ignorieren die Browser das download-Attribut und NAVIGIEREN: Die Anwendung
   * war weg. Jetzt oeffnet {@link openInvoiceTab} den Tab synchron im Klick und
   * leitet ihn erst nach dem Abruf um.
   *
   * @param invoice Die angeklickte Rechnung.
   */
  const handleDownloadInvoice = (invoice: IInvoices): void => {
    if (invoices === undefined) return;

    // Doppelklicksperre. Die Sperre selbst liegt in `openInvoiceTab` - hier
    // wird sie nur gefragt, damit der zweite Klick nicht den Ladezustand
    // umschreibt, waehrend der erste Abruf noch laeuft.
    if (isInvoiceTabOpening(invoice)) return;

    const key = invoiceLockKey(invoice);
    setOpenError(null);
    setOpeningKeys((current) => [...current, key]);

    void openInvoiceTab(invoice, fetchDocumentUrl).then((outcome) => {
      // Der Ladezustand endet mit dem Abruf, nicht nach einem festen
      // setTimeout: Seit der Beleg bei Bedarf erzeugt wird, dauert der erste
      // Abruf 5-7 Sekunden - jede feste Frist ist entweder zu kurz (der
      // Ladebalken verschwindet vor dem Beleg) oder zu lang.
      setOpeningKeys((current) => current.filter((entry) => entry !== key));

      // Nur setzen, wenn es etwas zu melden gibt: Sonst loeschte eine zweite,
      // gelungene Rechnung die Meldung der ersten, die daneben noch steht.
      const message = invoiceTabMessage(outcome);
      if (message !== null) setOpenError(message);
    });
  };
  useEffect(() => {
    if (currentUser.appUser.customer_id === null) return;
    getInvoices({ variables: { customer_id: currentUser.appUser.customer_id } });
  }, [getInvoices, currentUser.appUser.customer_id]);
  return (
    <>
      {openingKeys.length > 0 && <LoadingIndicator type={LoaderTypeEnums.PROGRESS} />}
      <StyledDownloadInvoices.Wrapper margin_bottom={true}>
        <Table width="100%">
          <thead>
            <Row>
              <Cell level="title" highlight width="50%">
                {" "}
                {t("quick/common:main_content.top_bar.user_profile.invoices.invoice_number")}
              </Cell>
              <Cell level="title" highlight>
                {" "}
                {t("quick/common:main_content.top_bar.user_profile.invoices.date")}
              </Cell>
              <Cell level="title" highlight>
                &nbsp;
              </Cell>
            </Row>
          </thead>
          <tbody>
            {invoices && invoices.length === 0 ? (
              <Row>
                <Cell>
                  {loading
                    ? "Wird geladen...."
                    : t("quick/common:main_content.top_bar.user_profile.invoices.no_invoice")}
                </Cell>
                <Cell>&nbsp;</Cell>
                <Cell>&nbsp;</Cell>
              </Row>
            ) : (
              invoices?.map((invoice, index) => {
                const { number, created } = invoice;
                return (
                  <Row key={index}>
                    <Cell>{number}</Cell>
                    <Cell>
                      {new Date(+created * 1000).toLocaleDateString(localeFromLanguage(), {
                        month: "2-digit",
                        year: "numeric",
                        day: "2-digit",
                      })}
                    </Cell>
                    <Cell align="right">
                      <IconButton
                        icon={<Icon size={IconSizeEnums.SMALL} name={IconNameEnums.Q_DOWNLOAD} />}
                        inverted={false}
                        variant={ButtonVariantEnums.DEFAULT}
                        type={"button"}
                        // Der Knopf ist gesperrt, solange diese Zeile wartet.
                        // Die verbindliche Sperre steht in `openInvoiceTab` -
                        // dies hier ist nur die sichtbare Haelfte davon.
                        disabled={openingKeys.includes(invoiceLockKey(invoice))}
                        onClick={(): void => {
                          handleDownloadInvoice(invoice);
                        }}
                      />
                    </Cell>
                  </Row>
                );
              })
            )}
          </tbody>
        </Table>
        {openError !== null && (
          // role="alert", damit ein Vorleseprogramm es mitbekommt: Der Beleg
          // sollte in einem anderen Tab erscheinen - wenn er das nicht tut,
          // gibt es hier sonst gar keine Rueckmeldung.
          <StyledDownloadInvoices.Message role="alert">{openError}</StyledDownloadInvoices.Message>
        )}
      </StyledDownloadInvoices.Wrapper>
    </>
  );
};
interface IUseInvoicesDialogRetVal {
  open: () => void;
}
export const useInvoicesDialog = (): IUseInvoicesDialogRetVal => {
  const dialogs = useDialogs();
  return {
    open: (): void => {
      dialogs.open({
        dialogKey: IDialogKeyEnums.SIMPLE,
        title: "Rechnungen",
        closable: true,
        content: <Content />,
        flexible: false,
      });
    },
  };
};
